<template>
  <PublicLayout>
    <div v-if="property" class="container">
      <nav class="breadcrumb" aria-label="مسار التنقل">
        <RouterLink to="/">الرئيسية</RouterLink>
        <span class="breadcrumb-sep">/</span>
        <RouterLink to="/properties">العقارات</RouterLink>
        <span class="breadcrumb-sep">/</span>
        <RouterLink v-if="area" :to="`/areas/${property.areaSlug}`">{{
          area.name
        }}</RouterLink>
        <span v-if="area" class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ property.title }}</span>
      </nav>

      <div class="detail-header">
        <div>
          <div class="header-badges">
            <span class="badge" :class="typeBadgeClass">{{ typeLabel }}</span>
            <span
              class="badge"
              :class="
                property.status === 'available'
                  ? 'badge-success'
                  : 'badge-warning'
              "
            >
              {{ property.status === "available" ? "متاح" : "قيد التفاوض" }}
            </span>
            <span class="ref muted">#{{ property.ref }}</span>
          </div>
          <h1>{{ property.title }}</h1>
          <p class="detail-location muted">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"
              />
            </svg>
            {{ property.location }}
          </p>
        </div>
        <p class="header-price">
          {{ formatPrice(property.price, property.type) }}
        </p>
      </div>

      <div class="detail-grid">
        <div class="detail-main">
          <PropertyGallery
            :images="property.images || [property.image]"
            :alt="property.title"
          />

          <div class="tabs card">
            <div class="tab-list" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                role="tab"
                class="tab-btn"
                :class="{ active: activeTab === tab.id }"
                :aria-selected="activeTab === tab.id"
                @click="activeTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>

            <div v-show="activeTab === 'overview'" class="tab-panel">
              <p>{{ property.longDescription || property.description }}</p>
              <div class="detail-meta">
                <div v-if="property.beds" class="meta-item">
                  <span class="meta-value">{{ property.beds }}</span>
                  <span class="meta-label muted">غرف نوم</span>
                </div>
                <div v-if="property.baths" class="meta-item">
                  <span class="meta-value">{{ property.baths }}</span>
                  <span class="meta-label muted">حمامات</span>
                </div>
                <div class="meta-item">
                  <span class="meta-value">{{ property.area }}</span>
                  <span class="meta-label muted">م²</span>
                </div>
                <div v-if="property.features?.parking" class="meta-item">
                  <span class="meta-value">{{
                    property.features.parking
                  }}</span>
                  <span class="meta-label muted">مواقف</span>
                </div>
              </div>
            </div>

            <div v-show="activeTab === 'features'" class="tab-panel">
              <div v-if="property.amenities?.length" class="amenities">
                <h3>المرافق</h3>
                <div class="amenity-tags">
                  <span
                    v-for="a in property.amenities"
                    :key="a"
                    class="amenity-tag"
                  >
                    {{ a }}
                  </span>
                </div>
              </div>
              <div v-if="property.features" class="features-table">
                <h3>المواصفات</h3>
                <dl>
                  <div v-if="property.features.yearBuilt">
                    <dt>سنة البناء</dt>
                    <dd>{{ property.features.yearBuilt }}</dd>
                  </div>
                  <div v-if="property.features.floors">
                    <dt>الطوابق</dt>
                    <dd>{{ property.features.floors }}</dd>
                  </div>
                  <div>
                    <dt>مفروش</dt>
                    <dd>{{ property.features.furnished ? "نعم" : "لا" }}</dd>
                  </div>
                  <div v-if="property.features.facing">
                    <dt>الاتجاه</dt>
                    <dd>{{ property.features.facing }}</dd>
                  </div>
                  <div>
                    <dt>النوع</dt>
                    <dd>{{ categoryLabels[property.category] }}</dd>
                  </div>
                  <div>
                    <dt>تاريخ الإدراج</dt>
                    <dd>{{ formatDate(property.listedAt) }}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div v-show="activeTab === 'location'" class="tab-panel">
              <div class="map-box">
                <svg
                  viewBox="0 0 24 24"
                  width="40"
                  height="40"
                  aria-hidden="true"
                >
                  <path
                    fill="var(--brand)"
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"
                  />
                </svg>
                <p>{{ property.location }}</p>
                <RouterLink
                  v-if="area"
                  :to="`/areas/${property.areaSlug}`"
                  class="btn btn-secondary btn-sm"
                >
                  استكشف {{ area.name }}
                </RouterLink>
              </div>
            </div>
          </div>
        </div>

        <aside class="detail-sidebar">
          <div class="card sidebar-card">
            <p class="sidebar-price">
              {{ formatPrice(property.price, property.type) }}
            </p>
            <hr class="divider" />
            <button
              type="button"
              class="btn btn-primary btn-block"
              @click="showContact = true"
            >
              طلب معاينة
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-block"
              @click="showContact = true"
            >
              استفسار
            </button>
            <RouterLink to="/properties" class="btn btn-ghost btn-block">
              العودة للقائمة
            </RouterLink>
          </div>
          <AgentCard v-if="property.agent" :agent="property.agent" />
        </aside>
      </div>

      <section v-if="similar.length" class="section">
        <div class="section-header">
          <h2>عقارات مشابهة</h2>
        </div>
        <div class="property-grid similar-grid">
          <PropertyCard v-for="p in similar" :key="p.id" :property="p" />
        </div>
      </section>
    </div>

    <div v-else class="container">
      <div class="empty-state card">
        <h3>العقار غير موجود</h3>
        <p class="muted">قد يكون هذا العقار قد أُزيل أو الرابط غير صحيح.</p>
        <RouterLink to="/properties" class="btn btn-primary"
          >تصفح العقارات</RouterLink
        >
      </div>
    </div>

    <div
      v-if="showContact"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="showContact = false"
    >
      <div class="modal card">
        <h2>تواصل بخصوص {{ property?.title }}</h2>
        <p class="muted">
          سيتواصل معك {{ property?.agent?.name }} خلال 24 ساعة.
        </p>
        <form @submit.prevent="submitContact">
          <div class="form-field">
            <label class="label" for="contact-name">الاسم</label>
            <input id="contact-name" class="input" type="text" required />
          </div>
          <div class="form-field">
            <label class="label" for="contact-phone">الهاتف</label>
            <input id="contact-phone" class="input" type="tel" required />
          </div>
          <div class="form-field">
            <label class="label" for="contact-msg">رسالتك</label>
            <textarea id="contact-msg" class="textarea" rows="3" required />
          </div>
          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-ghost"
              @click="showContact = false"
            >
              إلغاء
            </button>
            <button type="submit" class="btn btn-primary">إرسال</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="toast" class="toast" role="status" aria-live="polite">
      {{ toast }}
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute, RouterLink } from "vue-router";
import PublicLayout from "@/components/layout/PublicLayout.vue";
import PropertyGallery from "@/components/mkdf/PropertyGallery.vue";
import PropertyCard from "@/components/mkdf/PropertyCard.vue";
import AgentCard from "@/components/mkdf/AgentCard.vue";
import {
  getPropertyById,
  getSimilarProperties,
  formatPrice,
  categoryLabels,
} from "@/data/properties";
import { getAreaBySlug } from "@/data/areas";

const route = useRoute();
const property = computed(() => getPropertyById(route.params.id));
const area = computed(() =>
  property.value ? getAreaBySlug(property.value.areaSlug) : null
);
const similar = computed(() =>
  property.value ? getSimilarProperties(property.value) : []
);
const showContact = ref(false);
const toast = ref("");
const activeTab = ref("overview");

const tabs = [
  { id: "overview", label: "نظرة عامة" },
  { id: "features", label: "المرافق والمواصفات" },
  { id: "location", label: "الموقع" },
];

const typeLabel = computed(() =>
  property.value?.type === "rent" ? "للإيجار" : "للبيع"
);
const typeBadgeClass = computed(() =>
  property.value?.type === "rent" ? "badge-info" : "badge-success"
);

function formatDate(d) {
  if (!d) return "—";
  return new Intl.DateTimeFormat("ar-KW", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(d));
}

function submitContact() {
  showContact.value = false;
  toast.value = "تم إرسال استفسارك بنجاح!";
  setTimeout(() => {
    toast.value = "";
  }, 5000);
}

function onKeydown(e) {
  if (e.key === "Escape") showContact.value = false;
}

onMounted(() => document.addEventListener("keydown", onKeydown));
onUnmounted(() => document.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
.detail-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: var(--section-gap);
}

.detail-header h1 {
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  margin: 8px 0;
}

.header-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.ref {
  font-size: 0.8125rem;
}

.header-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--brand);
  margin: 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--section-gap);
  align-items: start;
}

@media (min-width: 1024px) {
  .detail-grid {
    grid-template-columns: 1fr 340px;
  }
}

.detail-sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--section-gap);
}

.tabs {
  padding: 0;
  overflow: hidden;
  margin-top: var(--section-gap);
}

.tab-list {
  display: flex;
  border-bottom: 1px solid var(--border);
  overflow-x: auto;
}

.tab-btn {
  padding: 14px 20px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--content-muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: color var(--motion), border-color var(--motion);
}

.tab-btn.active {
  color: var(--brand);
  border-bottom-color: var(--brand);
}

.tab-btn:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: -2px;
}

.tab-panel {
  padding: 24px;
}

.tab-panel p {
  line-height: 1.7;
  margin-bottom: 24px;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 32px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.meta-item {
  text-align: center;
  min-width: 64px;
}

.meta-value {
  display: block;
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--content-heading);
}

.meta-label {
  font-size: 0.8125rem;
}

.amenities h3,
.features-table h3 {
  font-size: 1rem;
  margin-bottom: 12px;
}

.amenity-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.amenity-tag {
  padding: 6px 12px;
  background: var(--brand-muted);
  color: var(--brand);
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
  font-weight: 500;
}

.features-table dl {
  margin: 0;
  display: grid;
  gap: 0;
}

.features-table dl > div {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.9375rem;
}

.features-table dt {
  color: var(--content-muted);
}

.features-table dd {
  margin: 0;
  font-weight: 500;
  color: var(--content-heading);
}

.map-box {
  text-align: center;
  padding: 48px 24px;
  background: var(--surface-page);
  border-radius: var(--radius-md);
}

.map-box p {
  margin: 12px 0 16px;
  font-weight: 500;
}

.sidebar-card {
  position: sticky;
  top: calc(var(--navbar-height) + 24px);
}

.sidebar-price {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--brand);
  margin: 0;
}

.sidebar-card .divider {
  margin-block: 16px;
}

.btn-block {
  width: 100%;
  margin-bottom: 8px;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 0.875rem;
}

.similar-grid {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  max-width: 480px;
  margin-inline: auto;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(34, 48, 62, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 16px;
}

.modal {
  width: 100%;
  max-width: 480px;
}

.form-field {
  margin-bottom: 16px;
}

.textarea {
  width: 100%;
  padding: 10px 12px;
  font-family: inherit;
  font-size: var(--text-base);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  resize: vertical;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.toast {
  position: fixed;
  bottom: 24px;
  inset-inline-end: 24px;
  background: var(--content-heading);
  color: #fff;
  padding: 12px 20px;
  border-radius: var(--radius-md);
  z-index: 300;
}
</style>
